// Shared utilities for Admin evaluation pages
import { Part, PostOverrideDetail, Question, SubQuestion } from './types'

export interface FlatRow {
  key: string
  qNo: string
  max: number | string
  evaluatorMarks: Record<
    string,
    { marks: number | null; oldMarks?: number | null; isRevision?: boolean }
  >
  indicators: {
    preEvaluatorDiff?: number | null
    hasPostOverride?: boolean
    postOverrideDetails?: PostOverrideDetail[]
  }
  /** If set, this row is a part-header separator row (not a question row) */
  isPartHeader?: boolean
  /** Part name for header rows */
  partName?: string
  /** Choice info for header rows */
  hasChoice?: boolean
  numberOfQuestionsToAttempt?: number | null
  totalQuestionsInPart?: number | null
}

/**
 * Flattens the parts/questions/subQuestions tree into a flat array of rows
 * suitable for a marks table. Used by both external and internal booklet detail pages.
 * Inserts part-header separator rows when there are multiple parts.
 *
 * ★ Insight ─────────────────────────────────────────────────────────────
 * - Question numbering format: parent as "1", sub-questions as "1(a)", "1(b)", etc.
 * - Subquestions are rendered separately from parent questions (not as trailing info)
 * - CampX precedent: campx-exams-web/src/pages/EvaluationEditor
 * - Watch out for: API might return questionIndex as string or number; use String()
 * ───────────────────────────────────────────────────────────────────────
 */
export function flattenPartsToRows(parts: Part[]): FlatRow[] {
  const rows: FlatRow[] = []
  const hasMultipleParts = (parts || []).length > 1
  ;(parts || []).forEach((part: Part) => {
    // Insert part header separator when multiple parts exist
    if (hasMultipleParts) {
      rows.push({
        key: `part-header-${part.partId}`,
        qNo: '',
        max: '',
        evaluatorMarks: {},
        indicators: {},
        isPartHeader: true,
        partName: part.partName || `Part ${part.partIndex}`,
        hasChoice: part.hasChoice ?? false,
        numberOfQuestionsToAttempt: part.numberOfQuestionsToAttempt ?? null,
        totalQuestionsInPart: (part.questions || []).length || null,
      })
    }
    ;(part.questions || []).forEach((question: Question) => {
      // Only add parent question row if it has no subQuestions OR subQuestions are empty
      const hasSubQuestions =
        question.subQuestions && question.subQuestions.length > 0
      const displayNo =
        question.questionText || String(question.questionIndex || '-')
      if (!hasSubQuestions) {
        rows.push({
          key: `q-${part.partId}-${question.questionId}`,
          qNo: displayNo,
          max: question.maxMarks ?? '-',
          evaluatorMarks: question.evaluatorMarks || {},
          indicators: {
            preEvaluatorDiff: question.indicators?.preEvaluatorDiff,
            hasPostOverride: question.indicators?.hasPostOverride,
            postOverrideDetails: question.indicators?.postOverrideDetails,
          },
        })
      }

      // Add rows for each subquestion
      ;(question.subQuestions || []).forEach(
        (sub: SubQuestion, subIdx: number) => {
          rows.push({
            key: `sq-${part.partId}-${question.questionId}-${sub.subQuestionId}`,
            qNo: `${displayNo}(${String.fromCharCode(97 + subIdx)})`,
            max: sub.maxMarks ?? '-',
            evaluatorMarks: sub.evaluatorMarks || {},
            indicators: {
              preEvaluatorDiff: sub.indicators?.preEvaluatorDiff,
              hasPostOverride: sub.indicators?.hasPostOverride,
              postOverrideDetails: sub.indicators?.postOverrideDetails,
            },
          })
        },
      )
    })
  })
  return rows
}

/**
 * Returns cell background color based on variance/admin override indicators.
 * For external booklet detail and diff report.
 * Unevaluated cells ("—") always get transparent regardless of type.
 *
 * Color mapping:
 * - Variance: #FFFACD (warning.light)
 * - Admin override: #D1E7FD (primary.light)
 * - No changes: #E8F5E9 (success.light)
 * - Scrutinizer: always transparent (no highlighting)
 * - Unevaluated: always transparent
 */
export function getCellBg(row: FlatRow, evaluatorType?: string): string {
  const type = evaluatorType?.toLowerCase()
  const cellValue = evaluatorType
    ? row.evaluatorMarks?.[evaluatorType]
    : undefined
  const hasMarks =
    cellValue && cellValue.marks !== null && cellValue.marks !== undefined

  if (type === 'scrutinizer') return 'transparent'
  // Only color cells that have been evaluated
  if (!hasMarks) return 'transparent'
  // Check if this evaluator's marks were changed by admin
  const overrides = row.indicators?.postOverrideDetails || []
  const wasChangedByAdmin = overrides.some(
    (d) => d.evaluatorType === evaluatorType && d.changedBy === 'Admin',
  )
  if (wasChangedByAdmin) return '#D1E7FD'
  // Admin column with no override — transparent
  if (type === 'admin') return 'transparent'
  const diff = row.indicators?.preEvaluatorDiff
  if (diff !== null && diff !== undefined && diff !== 0) return '#FFFACD'
  return '#E8F5E9'
}

/**
 * Returns cell background color for internal booklet detail.
 * Internal has no admin override concept — only variance/no-changes.
 */
export function getCellBgInternal(row: FlatRow): string {
  const diff = row.indicators?.preEvaluatorDiff
  if (diff !== null && diff !== undefined && diff !== 0) return '#FFFACD'
  return '#E8F5E9'
}

export const DEFAULT_PAGE_SIZE = 10
export const PAGE_SIZE_OPTIONS = [10, 20, 50]
